import { ReactNode } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import { Icon } from '@iconify/react';
import { Overline } from 'components/InterviewResults';

type Props = {
  open: boolean;
  /** Вызывается по кнопке/закрытию — родитель закрывает окно. */
  onConfirm: () => void;
};

type FeatureRowProps = {
  icon: string;
  color: string;
  title: string;
  description: ReactNode;
};

const FeatureRow = ({ icon, color, title, description }: FeatureRowProps) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <Box
      sx={{
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: alpha(color, 0.12),
        color
      }}
    >
      <Icon icon={icon} width={22} height={22} />
    </Box>
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  </Stack>
);

/**
 * Вступительное окно перед самооценкой навыков. Объясняет, откуда взялись навыки
 * (ИИ определил их по профилям похожих специалистов и короткой беседе), что по ним
 * будут сформированы вопросы основного интервью, и зачем нужна самооценка.
 */
const SkillLevelIntroModal = ({ open, onConfirm }: Props) => {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onConfirm} scroll="body" aria-labelledby="skill-level-intro-title">
      <DialogTitle id="skill-level-intro-title">
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
          <Typography textAlign="center" variant="h6">
            Самооценка навыков
          </Typography>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: 'primary.main'
            }}
          >
            <Icon icon="mdi:chart-bar" width={32} height={32} />
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 3, sm: 4 } }}>
        <Stack spacing={5}>
          {/* Заголовок */}
          <Stack spacing={1.5} alignItems="center" textAlign="center">
            <Typography sx={{ mt: '16px !important' }} variant="body2" color="text.secondary">
              Перед вами навыки, которые искусственный интеллект определил специально для вас.
              Оцените, насколько вы владеете каждым из них, — это займёт пару минут.
            </Typography>
          </Stack>

          {/* Откуда навыки и что с ними будет */}
          <Box>
            <Overline>Как всё устроено</Overline>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FeatureRow
                icon="mdi:account-search-outline"
                color={theme.palette.primary.main}
                title="Откуда эти навыки"
                description="ИИ собрал их из профилей специалистов с похожей должностью и опытом, а также из короткой беседы с вами."
              />
              <FeatureRow
                icon="mdi:comment-question-outline"
                color={theme.palette.primary.main}
                title="Что будет дальше"
                description="По этим навыкам будут сформированы вопросы основного интервью."
              />
              <FeatureRow
                icon="mdi:gesture-tap-button"
                color={theme.palette.info.main}
                title="Что нужно сделать"
                description="Оцените каждый навык по шкале от 1 до 10 — так, как вы сами оцениваете свой уровень."
              />
            </Stack>
          </Box>

          {/* Зачем это нужно */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: `1px solid ${alpha(theme.palette.success.main, 0.24)}`
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ color: 'success.main', mt: '2px', flexShrink: 0 }}>
                <Icon icon="mdi:tune-variant" width={22} height={22} />
              </Box>
              <Typography variant="body1" color="text.secondary">
                Система подстроится под вашу самооценку и в итоге сравнит её с результатами
                интервью — это повлияет на оценку уверенности в себе и другие параметры отчёта.
                Правильных ответов нет, отвечайте честно.
              </Typography>
            </Stack>
          </Box>

          <Button size="large" fullWidth variant="contained" onClick={onConfirm}>
            Понятно, начать самооценку
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SkillLevelIntroModal;
